import React, { lazy, Suspense } from 'react'
import { BrowserRouter as Brouter, Route, Switch, Redirect } from 'react-router-dom'

import Main from '../components/Main'
import { useReactiveVar } from '@apollo/client'
import { appSubdomainVar } from '../graphql/stores/local'
import { SCHOOL_LEIDERCHAP_SUBDOMAIN_NAME } from '../constants'
import * as internalRoutes from '../constants/internaRoutes'
const Uhasselt = lazy(() => import('./Research/Uhasselt'))

const Group = lazy(() => import('./Views/Group/Group'))

const PrivateRoute = lazy(() => import('../components/PrivateRoute'))
const Home = lazy(() => import('./Home'))
const GoogleLogin = lazy(() => import('./GoogleLogin'))
const NotFoundPage = lazy(() => import('./NotFoundPage'))
const UnauthorizedPage = lazy(() => import('./UnauthorizedPage'))

const RegisterSchool = lazy(() => import('./Create/RegisterSchool'))
const UpdateSchool = lazy(() => import('./Create/UpdateSchool'))
const CreateAnswer = lazy(() => import('./Create/CreateAnswer'))
const CreateQuestion = lazy(() => import('./Create/CreateQuestion'))
const CreateSLQuestion = lazy(() => import('./Create/CreateSLQuestion'))
const CreateForm = lazy(() => import('./Create/CreateForm'))
const CreateGroup = lazy(() => import('./Create/CreateSchoolleaderGroup'))
const CreateSession = lazy(() => import('./Create/CreateSession'))
const CreateSchoolLeader = lazy(() => import('./Create/CreateSchoolLeader'))
const UpdateSession = lazy(() => import('./Create/UpdateSession'))
const UpdateForm = lazy(() => import('./Create/UpdateForm'))
const UpdateSchoolLeader = lazy(() => import('./Create/UpdateSchoolLeader'))
const CreateStatisticGroups = lazy(() => import('./Create/CreateStatisticGroup'))
const CreateMultischoolReport = lazy(() => import('./Create/CreateMultischoolReport'))

const Answers = lazy(() => import('./Lists/Answers'))
const Forms = lazy(() => import('./Lists/Forms'))
const Groups = lazy(() => import('./Lists/Groups'))
const ParticipantAnswers = lazy(() => import('./Lists/ParticipantAnswers'))
const Questions = lazy(() => import('./Lists/Questions'))
const Reports = lazy(() => import('./Lists/DigikapitaalReports'))
const SchoolleaderReports = lazy(() => import('./Lists/SchoolleaderReports'))
const Schools = lazy(() => import('./Lists/Schools'))
const SchoolLeaders = lazy(() => import('./Lists/SchoolLeaders'))
const Sessions = lazy(() => import('./Lists/Sessions'))
const StatisticGroups = lazy(() => import('./Lists/StatisticsGroups'))

const ViewSchool = lazy(() => import('./Views/ViewSchool'))
const ParticipantAnswer = lazy(() => import('./Views/ParticipantAnswer'))
const UserAnswers = lazy(() => import('./UserAnswers'))
const UserSLAnswers = lazy(() => import('./UserSLAnswers'))
const UpdateAnswer = lazy(() => import('./Create/UpdateAnswer'))
const UpdateQuestion = lazy(() => import('./Create/UpdateQuestion'))
const UpdateSLQuestion = lazy(() => import('./Create/UpdateSLQuestion'))
const Dashboard = lazy(() => import('./Views/Dashboard'))
const DashboardSL = lazy(() => import('./Views/DashboardSL'))
const RegisterSchoolLeader = lazy(() => import('./Create/RegisterSchoolleader'))
// update statistics group route
const UpdateStatisticGroup = lazy(() => import('./Create/UpdateStatisticGroup'))
const QuickScanParticipants = lazy(() => import('./Lists/QuickScan/QuickScan.Participants'))

// anonymous schoolleaders
const AnonymousSchoolleaders = lazy(() =>
  import('./Lists/PreregisteredSchoolleaders/PreregisteredSchoolleaders')
)
const PreregisterSchoolleader = lazy(() => import('./Create/PreregisterSchoolleader'))

export function Router() {
  const subdomain = useReactiveVar(appSubdomainVar)
  const adminRedirectRoute =
    subdomain === SCHOOL_LEIDERCHAP_SUBDOMAIN_NAME ? `/admin/schoolleaders` : '/admin/schools'
  return (
    <Suspense fallback={<Brouter />}>
      <Brouter>
        <Main>
          <Switch>
            <Route exact path="/" component={Home} />
            <Route
              path={internalRoutes.login}
              component={() => <GoogleLogin subdomain={subdomain} />}
            />
            <Route path={internalRoutes.registerSchool} component={RegisterSchool} />
            <Route path={internalRoutes.digiscan} component={UserAnswers} />
            <Route path={internalRoutes.digiscanRoot} component={UserAnswers} />
            <Route
              path={internalRoutes.registerSchoolleader}
              component={() => <RegisterSchoolLeader subdomain={subdomain} />}
            />
            <Route
              exact={true}
              path={internalRoutes.preregisterSchoolleader}
              component={PreregisterSchoolleader}
            />
            <Route
              exact={true}
              path={internalRoutes.preregisterSchoolleaderRoot}
              component={PreregisterSchoolleader}
            />
            <Route path={internalRoutes.schoolleaderSurvey} component={UserSLAnswers} />

            {/*Admin paths*/}

            {/*Lists*/}
            <PrivateRoute exact path={internalRoutes.list.dashboard} component={Dashboard} />
            <PrivateRoute
              exact
              path={internalRoutes.list.schoolleaderDashboard}
              component={DashboardSL}
            />
            <PrivateRoute
              exact
              path={internalRoutes.list.quicScanParticipants}
              component={QuickScanParticipants}
            />
            <PrivateRoute exact path={internalRoutes.list.schools} component={Schools} />
            <PrivateRoute exact path={internalRoutes.list.answers} component={Answers} />
            <PrivateRoute exact path={internalRoutes.list.sessions} component={Sessions} />
            <PrivateRoute exact path={internalRoutes.list.reports} component={Reports} />
            <PrivateRoute
              exact
              path={internalRoutes.list.scholleaderReports}
              component={SchoolleaderReports}
            />
            <PrivateRoute exact path={internalRoutes.list.questions} component={Questions} />
            <PrivateRoute
              exact
              path={internalRoutes.list.formAnswers}
              component={ParticipantAnswers}
            />
            <PrivateRoute exact path={internalRoutes.list.forms} component={Forms} />
            <PrivateRoute
              exact
              path={internalRoutes.list.schoolleaders}
              component={SchoolLeaders}
            />
            <PrivateRoute exact path={internalRoutes.list.groups} component={Groups} />
            <PrivateRoute
              exact
              path={internalRoutes.list.statisticGroups}
              component={StatisticGroups}
            />
            <PrivateRoute
              exact
              path={internalRoutes.list.anonymousSchoolleaders}
              component={AnonymousSchoolleaders}
            />

            {/*create*/}
            <PrivateRoute path={internalRoutes.create.answer} component={CreateAnswer} />
            <PrivateRoute path={internalRoutes.create.form} component={CreateForm} />
            <PrivateRoute path={internalRoutes.create.group} component={CreateGroup} />
            <PrivateRoute path={internalRoutes.create.question} component={CreateQuestion} />
            <PrivateRoute
              path={internalRoutes.create.schoolleaderQuestion}
              component={CreateSLQuestion}
            />
            <PrivateRoute path={internalRoutes.create.session} component={CreateSession} />
            <PrivateRoute
              path={internalRoutes.create.schoolleader}
              component={CreateSchoolLeader}
            />
            <PrivateRoute
              path={internalRoutes.create.statisticGroups}
              component={CreateStatisticGroups}
            />
            <PrivateRoute
              path={internalRoutes.create.multischoolReport}
              component={CreateMultischoolReport}
            />

            {/*View / update*/}
            <PrivateRoute path={internalRoutes.view.research.root} component={Uhasselt} />
            <PrivateRoute path={internalRoutes.view.research.uhasselt} component={Uhasselt} />
            <PrivateRoute path={internalRoutes.view.formAnswers} component={ParticipantAnswer} />
            <PrivateRoute path={internalRoutes.update.session} component={UpdateSession} />
            <PrivateRoute path={internalRoutes.update.answer} component={UpdateAnswer} />
            <PrivateRoute path={internalRoutes.update.form} component={UpdateForm} />
            <PrivateRoute path={internalRoutes.update.school} component={UpdateSchool} />
            <PrivateRoute path={internalRoutes.view.school} component={ViewSchool} />
            <PrivateRoute path={internalRoutes.update.question} component={UpdateQuestion} />
            <PrivateRoute
              path={internalRoutes.update.schoolleaderQuestion}
              component={UpdateSLQuestion}
            />
            <PrivateRoute path={internalRoutes.view.group} component={Group} />

            <PrivateRoute
              path={internalRoutes.update.schoolleader}
              component={UpdateSchoolLeader}
            />

            <PrivateRoute
              path={internalRoutes.update.statisticGroup}
              component={UpdateStatisticGroup}
            />

            <PrivateRoute
              exact
              path={internalRoutes.admin}
              component={() => <Redirect to={adminRedirectRoute} />}
            />

            <Route path={internalRoutes.unauthorized} component={UnauthorizedPage} />

            <Route path="*" component={NotFoundPage} />
          </Switch>
        </Main>
      </Brouter>
    </Suspense>
  )
}
